<template>
  <v-dialog v-model="internalValue" width="550">
    <v-card class="rounded-lg">
      <!-- Header -->
      <v-card-title class="d-flex justify-space-between align-center pa-4">
        <span class="headline font-weight-bold">
          <v-icon left color="primary">mdi-robot</v-icon>
          {{ $t('management.botModal.title') }} — {{ company ? company.name : '' }}
        </span>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-4">
        <!-- Phone Edit Form (only if unlinked) -->
        <div v-if="linkStatus === 'unlinked'" class="mb-4">
          <div class="subtitle-2 font-weight-bold mb-1">{{ $t('management.botModal.companyPhone') }}</div>
          <div class="caption grey--text mb-2">{{ $t('management.botModal.phoneHint') }}</div>
          <div class="d-flex align-start flex-gap-2">
            <v-text-field
              v-model="phoneInput"
              outlined
              dense
              hide-details="auto"
              placeholder="+5511999998888"
              prepend-inner-icon="mdi-phone"
              :rules="phoneRules"
            ></v-text-field>
            <v-btn
              color="primary"
              outlined
              :loading="savingPhone"
              :disabled="!isPhoneInputValid"
              @click="savePhone"
              style="margin-top: 2px;"
            >
              {{ $t('management.botModal.save') }}
            </v-btn>
          </div>
        </div>

        <v-divider v-if="linkStatus === 'unlinked'" class="my-4"></v-divider>


        <!-- Quotebot Connection Section (Always visible) -->
        <div>
          <!-- Section Header / Status Row -->
          <div class="d-flex align-center flex-gap-2 mb-3">
            <span class="subtitle-2 font-weight-bold">{{ $t('management.botModal.quotebotLinkTitle') }}</span>
            <v-chip small :color="statusColor" class="white--text font-weight-medium">
              {{ statusText }}
            </v-chip>
          </div>

          <!-- 1. Linked State Details -->
          <div v-if="linkStatus === 'linked'" class="d-flex flex-column flex-gap-2 pl-1 mb-4">
            <!-- Link Time -->
            <div v-if="linkedAtFormatted" class="d-flex align-center">
              <v-icon left small color="grey" class="mr-2">mdi-clock-outline</v-icon>
              <span class="caption grey--text">
                {{ $t('management.botModal.linkedAt') }} <strong class="white--text">{{ linkedAtFormatted }}</strong>
              </span>
            </div>

            <!-- Phone Number -->
            <div class="d-flex align-center">
              <v-icon left small color="grey" class="mr-2">mdi-phone</v-icon>
              <span class="caption grey--text">
                {{ $t('management.botModal.companyPhone') }}: <strong class="white--text">{{ linkData.phone || '-' }}</strong>
              </span>
            </div>

            <!-- Telegram ID -->
            <div v-if="telegramUserId" class="d-flex align-center">
              <v-icon left small color="grey" class="mr-2">mdi-send</v-icon>
              <span class="caption grey--text">
                {{ $t('management.botModal.telegramUserId') }} <strong class="white--text">{{ telegramUserId }}</strong>
              </span>
            </div>

            <!-- Unlink: the only action available once a link is established -->
            <div class="d-flex align-center justify-space-between pt-2">
              <v-btn
                color="error"
                outlined
                small
                :loading="unlinking"
                @click="confirmUnlinkModal = true"
              >
                <v-icon left small>mdi-link-variant-off</v-icon>
                {{ $t('management.botModal.unlink') }}
              </v-btn>
              <span class="caption grey--text pr-4">{{ $t('management.botModal.unlinkHint') }}</span>
            </div>
          </div>

          <!-- 2. Token Active State (Pending) -->
          <div v-else-if="hasActiveToken">
            <!-- Inline row for Expiration, Phone and Tooltipped Icon Buttons -->
            <div class="d-flex flex-column flex-gap-2 mb-4">
              <div class="d-flex align-center justify-space-between flex-wrap">
                <div class="d-flex align-center">
                  <v-icon left small color="grey" class="mr-2">mdi-clock-outline</v-icon>
                  <span class="caption grey--text">
                    {{ $t('management.botModal.expiresAt') }}: <strong class="white--text">{{ tokenExpiresAtFormatted }}</strong>
                  </span>
                </div>
                <div class="d-flex align-center">
                  <!-- Refresh Button -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        icon
                        color="grey"
                        :loading="issuingToken"
                        @click="generateToken"
                      >
                        <v-icon>mdi-refresh</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('management.botModal.tokenRefresh') }}</span>
                  </v-tooltip>

                  <!-- Revoke Button -->
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        v-bind="attrs"
                        v-on="on"
                        icon
                        color="error"
                        :loading="revokingToken"
                        @click="revokeToken"
                      >
                        <v-icon>mdi-delete-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('management.botModal.tokenRevoke') }}</span>
                  </v-tooltip>
                </div>
              </div>

              <!-- Muted Phone row in pending state -->
              <div class="d-flex align-center">
                <v-icon left small color="grey" class="mr-2">mdi-phone</v-icon>
                <span class="caption grey--text">
                  {{ $t('management.botModal.companyPhone') }}: <strong class="white--text">{{ linkData.phone || '-' }}</strong>
                </span>
              </div>
            </div>

            <!-- Delivery Options -->
            <div class="subtitle-2 font-weight-bold mb-2">{{ $t('management.botModal.deliveryOptions') }}</div>

            <!-- Option 1: Email Delivery -->
            <div class="d-flex align-center justify-space-between py-2">
              <div>
                <div class="font-weight-medium body-2">{{ $t('management.botModal.sendEmail') }}</div>
                <div class="caption grey--text">{{ $t('management.botModal.sendEmailDescription', { email: company ? company.email : '-' }) }}</div>
              </div>
              <v-btn
                color="primary"
                small
                depressed
                :loading="sendingEmail"
                :disabled="!company"
                @click="sendEmailOnly"
              >
                <v-icon left small>mdi-email-send</v-icon>
                {{ $t('management.botModal.sendEmailBtn') }}
              </v-btn>
            </div>

            <!-- "ou" Divider -->
            <div class="d-flex align-center">
              <v-divider></v-divider>
              <span class="px-3 caption grey--text text-uppercase font-weight-bold">{{ $t('management.botModal.or') }}</span>
              <v-divider></v-divider>
            </div>

            <!-- Option 2: Copy Deep Link directly -->
            <div class="d-flex align-center justify-space-between py-2">
              <div>
                <div class="font-weight-medium body-2">{{ $t('management.botModal.copyTelegramLink') }}</div>
                <div class="caption grey--text">{{ $t('management.botModal.clickToCopy') }}</div>
                <!-- Which bot the invitation is for. Reported by the running
                     bot, so an invitation pointing at the wrong environment is
                     visible here instead of only in the recipient's Telegram. -->
                <div v-if="botUsername" class="caption grey--text">
                  <v-icon x-small color="grey" class="mr-1">mdi-robot-outline</v-icon>@{{ botUsername }}
                </div>
              </div>
              <v-btn
                color="primary"
                outlined
                small
                @click="copyLink"
              >
                <v-icon left small>mdi-content-copy</v-icon>
                {{ $t('management.botModal.copyTelegramLink') }}
              </v-btn>
            </div>
          </div>

          <!-- 3. Token inactive state (Unlinked) -->
          <div v-else class="d-flex align-center justify-space-between py-2">
            <span class="caption grey--text pr-4">{{ $t('management.botModal.generateLinkHint') }}</span>
            <v-btn
              depressed
              color="primary"
              :loading="issuingToken"
              :disabled="!company || !linkData.phone"
              @click="generateToken"
            >
              <v-icon left>mdi-key-plus</v-icon>
              {{ $t('management.botModal.generateLink') }}
            </v-btn>
          </div>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Actions -->
      <v-card-actions class="pa-3 d-flex justify-end">
        <v-btn outlined text @click="close">{{ $t('management.botModal.close') }}</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Unlink confirmation. Worth a stop: it ends the user's access straight
         away and cancels whatever operation they have in flight. -->
    <v-dialog v-model="confirmUnlinkModal" width="420">
      <v-card class="pa-4 d-flex align-center flex-column justify-center text-center">
        <v-card-title class="font-weight-bold">{{ $t('management.botModal.unlinkConfirmTitle') }}</v-card-title>
        <v-card-subtitle>{{ $t('management.botModal.unlinkConfirmBody') }}</v-card-subtitle>
        <div class="d-flex flex-gap-2 justify-end">
          <v-btn outlined color="primary" :disabled="unlinking" @click="confirmUnlinkModal = false">
            {{ $t('management.botModal.close') }}
          </v-btn>
          <v-btn color="error" :loading="unlinking" @click="unlink">
            {{ $t('management.botModal.unlink') }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
      {{ snackbar.message }}
    </v-snackbar>
  </v-dialog>
</template>

<script>

import { useAdminStore } from "@/store/admin";
import { formatTimeAndDate, phoneError } from "@/utils";

// Why a number was refused, in the user's language. The browser-side check and
// the platform answer with the same codes, so one map serves both.
const PHONE_ERROR_KEYS = {
  letters: "management.botModal.phoneNoLetters",
  missing_country_code: "management.botModal.phoneNeedsCountryCode",
  missing_ddd: "management.botModal.phoneMissingDdd",
  bad_length: "management.botModal.phoneInvalidLength",
  no_digits: "management.botModal.phoneInvalidLength"
};

// The shape the API answers with, for every "we have nothing yet" case. One
// definition because there were five, and a field added to only four of them is
// exactly how the deep link would have been missed.
function emptyLinkData() {
  return {
    status: "unlinked",
    phone: "",
    telegram_user_id: null,
    linked_at: null,
    pending_link: null,
    deep_link: "",
    bot_username: ""
  };
}

export default {
  name: "CompanyPhoneLinkModal",
  props: {
    value: {
      type: Boolean,
      default: false
    },
    company: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      phoneInput: "",
      savingPhone: false,
      issuingToken: false,
      revokingToken: false,
      unlinking: false,
      confirmUnlinkModal: false,
      sendingEmail: false,
      loadingLinkData: false,
      linkData: emptyLinkData(),
      snackbar: {
        show: false,
        message: "",
        color: "success"
      }
    };
  },
  computed: {
    adminStore() {
      return useAdminStore();
    },
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },
    linkStatus() {
      return this.linkData.status || "unlinked";
    },
    telegramUserId() {
      return this.linkData.telegram_user_id || null;
    },
    linkedAtFormatted() {
      if (!this.linkData.linked_at) return null;
      return formatTimeAndDate(this.linkData.linked_at);
    },
    statusColor() {
      if (this.linkStatus === "linked") return "green";
      if (this.linkStatus === "pending") return "orange darken-2";
      return "grey";
    },
    statusText() {
      if (this.linkStatus === "linked") return this.$t("management.botModal.statusLinked");
      if (this.linkStatus === "pending") return this.$t("management.botModal.statusPending");
      return this.$t("management.botModal.statusUnlinked");
    },
    hasActiveToken() {
      return !!(this.linkData && this.linkData.pending_link && this.linkData.pending_link.token);
    },
    tokenExpiresAtFormatted() {
      if (!this.hasActiveToken || !this.linkData.pending_link.expires_on) return "";
      return formatTimeAndDate(this.linkData.pending_link.expires_on);
    },
    deepLink() {
      // Built by the backend from the username the running bot reported.
      return this.linkData.deep_link || "";
    },
    botUsername() {
      return this.linkData.bot_username || "";
    },
    isPhoneInputValid() {
      return !phoneError(this.phoneInput);
    },
    phoneRules() {
      return [
        v => {
          if (!v) return true;
          const error = phoneError(v);
          if (!error) return true;
          return this.$t(PHONE_ERROR_KEYS[error] || "management.botModal.phoneInvalidLength");
        }
      ];
    }
  },
  watch: {
    company: {
      immediate: true,
      handler(newComp) {
        if (newComp) {
          this.fetchLinkData(newComp.id);
        } else {
          this.phoneInput = "";
          this.linkData = emptyLinkData();
        }
      }
    }
  },
  methods: {
    close() {
      this.internalValue = false;
    },
    showMsg(msg, color = "success") {
      this.snackbar.message = msg;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
    // Every call here fails the same way, and the server's `detail` is the only
    // part worth reading, so it is unpacked once.
    showError(err, messageKey, context) {
      console.error(context, err);
      // A structured detail (the phone refusals) is handled by its own caller;
      // only a plain string is worth appending to a message.
      const raw = err?.response?.data?.detail;
      const detail = typeof raw === "string" && raw ? ": " + raw : "";
      this.showMsg(this.$t(messageKey) + detail, "error");
    },
    async fetchLinkData(companyId) {
      if (!companyId) return;
      this.loadingLinkData = true;
      try {
        const res = await this.adminStore.getCompanyBotLinkStatus(companyId);
        this.linkData = res.data || emptyLinkData();
        this.phoneInput = this.linkData.phone || "";
      } catch (err) {
        console.error("Error fetching link data:", err);
        this.linkData = emptyLinkData();
        this.phoneInput = "";
      } finally {
        this.loadingLinkData = false;
      }
    },
    async savePhone() {
      if (!this.company) return;
      this.savingPhone = true;
      try {
        // Sent as typed: the platform is what normalizes, and what it echoes
        // back is what is really stored -- including Brazil's ninth digit.
        const res = await this.adminStore.updateCompanyPhone(this.company.id, this.phoneInput);
        this.linkData = res.data || { ...emptyLinkData(), phone: this.phoneInput };
        this.phoneInput = this.linkData.phone || this.phoneInput;
        this.showMsg(this.$t("management.botModal.phoneSaved"));
        this.$emit("updated");
      } catch (err) {
        this.showPhoneError(err);
      } finally {
        this.savingPhone = false;
      }
    },
    // The platform refuses a number for reasons the user can act on, so they
    // are named instead of folded into "error saving phone".
    showPhoneError(err) {
      const detail = err?.response?.data?.detail;
      const code = detail && detail.code;
      if (code === "phone_conflict") {
        this.showMsg(
          this.$t("management.botModal.phoneDuplicate", { company: detail.company_name || "" }),
          "error"
        );
      } else if (code === "phone_locked") {
        this.showMsg(this.$t("management.botModal.phoneLocked"), "error");
      } else if (code === "phone_invalid") {
        this.showMsg(
          this.$t(PHONE_ERROR_KEYS[detail.reason] || "management.botModal.phoneSaveError"),
          "error"
        );
      } else {
        this.showError(err, "management.botModal.phoneSaveError", "Error saving phone:");
      }
    },
    async generateToken() {
      if (!this.company) return;
      this.issuingToken = true;
      try {
        await this.adminStore.issueBotLinkingToken(this.company.id);
        await this.fetchLinkData(this.company.id);
        this.showMsg(this.$t("management.botModal.linkGenerated"));
        this.$emit("updated");
      } catch (err) {
        this.showError(err, "management.botModal.linkGenError", "Error issuing token:");
      } finally {
        this.issuingToken = false;
      }
    },
    async revokeToken() {
      if (!this.company) return;
      this.revokingToken = true;
      try {
        await this.adminStore.revokeBotLinkingToken(this.company.id);
        await this.fetchLinkData(this.company.id);
        this.showMsg(this.$t("management.botModal.tokenRevoked"));
        this.$emit("updated");
      } catch (err) {
        this.showError(err, "management.botModal.tokenRevokedError", "Error revoking token:");
      } finally {
        this.revokingToken = false;
      }
    },
    async unlink() {
      if (!this.company) return;
      this.unlinking = true;
      try {
        await this.adminStore.unlinkCompanyBot(this.company.id);
        // Re-reads rather than patching locally: the phone survives an unlink
        // and the modal needs to land in the unlinked state still holding it.
        await this.fetchLinkData(this.company.id);
        this.showMsg(this.$t("management.botModal.unlinked"));
        this.$emit("updated");
      } catch (err) {
        this.showError(err, "management.botModal.unlinkError", "Error unlinking company:");
      } finally {
        this.unlinking = false;
        this.confirmUnlinkModal = false;
      }
    },
    async sendEmailOnly() {
      if (!this.company || !this.hasActiveToken) return;
      this.sendingEmail = true;
      try {
        if (this.company.email && this.company.email !== '-') {
          await this.adminStore.sendOnboardingEmail({
            companyId: this.company.id,
            recipientEmail: this.company.email
          });
          this.showMsg(this.$t("management.botModal.linkSentEmail"));
        } else {
          this.showMsg(this.$t("management.botModal.linkGeneratedNoEmail"), "warning");
        }
      } catch (err) {
        this.showError(err, "management.botModal.linkSendError", "Error sending onboarding email:");
      } finally {
        this.sendingEmail = false;
      }
    },
    copyLink() {
      if (!this.deepLink) return;
      navigator.clipboard.writeText(this.deepLink);
      this.showMsg(this.$t("management.botModal.linkCopied"));
    }
  }
};
</script>

<style scoped>
.border {
  border: 1px solid rgba(0, 0, 0, 0.12);
}
.flex-gap-2 {
  gap: 8px;
}
</style>
